// src/design-system/Modal.tsx
import { X } from "lucide-react";
import { type ReactNode, useEffect, useRef } from "react";

/** نافذة منبثقة بسيطة وقابلة للوصول: Esc للإغلاق، وتركيز أول عند الفتح. */
export function Modal({
  open, title, onClose, children, footer, width = 520,
}: { open: boolean; title: string; onClose: () => void; children: ReactNode; footer?: ReactNode; width?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  // onClose غالباً دالة سهم مكتوبة inline في مكان الاستدعاء (زي onClose={() => setOpen(false)})،
  // فهويتها بتتغيّر مع كل ريندر للأب — يعني مع كل حرف يتكتب في أي خانة جوّه المودال (الأب بيعيد
  // الريندر). لو onClose في مصفوفة اعتماديات الـ effect، الـ effect كان بيتكرر مع كل حرف ويعيد
  // .focus() على أول عنصر قابل للتركيز في المودال — يعني التركيز بيتخطف من الخانة اللي بتكتب فيها
  // فعلياً ويرجع لأول خانة، وده اللي حاسس إنه "بيفقد التركيز بعد حرف واحد". الحل: نحتفظ بآخر نسخة من
  // onClose في ref بدل ما تدخل مصفوفة الاعتماديات، فالـ effect (وده اللي بيعمل .focus()) يشتغل بس
  // لما open نفسها تتغيّر (فتح/قفل المودال)، مش مع كل ريندر عادي.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onCloseRef.current();
    document.addEventListener("keydown", onKey);
    ref.current?.querySelector<HTMLElement>("input, select, textarea, button")?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4" style={{ background: "rgba(2,8,14,.72)", backdropFilter: "blur(4px)" }}
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={ref} role="dialog" aria-modal="true" aria-label={title} className="glass max-h-[90vh] w-full overflow-auto"
        style={{ maxWidth: width, boxShadow: "0 0 0 1px rgba(214,172,85,.3), 0 30px 80px rgba(0,0,0,.6), 0 0 60px rgba(214,172,85,.12)" }}>
        <header className="flex items-center justify-between border-b border-line px-5 py-3.5">
          <h2 className="m-0 text-[16px] font-semibold text-gold-xl">{title}</h2>
          <button type="button" aria-label="إغلاق" onClick={onClose} className="text-muted hover:text-ink"><X size={18} /></button>
        </header>
        <div className="flex flex-col gap-4 p-5">{children}</div>
        {footer && <footer className="flex justify-end gap-2.5 border-t border-line px-5 py-3.5">{footer}</footer>}
      </div>
    </div>
  );
}
